/**
 * ClientsBlock — MPV1 (multi-provider-agents) + same-day follow-ups. Machine-
 * level "Providers" list: every AI credential the daemon manages, in ONE
 * place — Anthropic (native, no key), ZAI/GLM, and Codex/Gemini (each their
 * own API key). Toggling a provider on + pasting its key (when it needs
 * one) makes it selectable in a project's team member editor.
 *
 * 2026-07-09 simplification — operator feedback: an API key is the only
 * thing an operator should ever have to provide here. The endpoint/small-
 * model details ZAI needs (base URL, background model) are implementation
 * defaults the daemon already knows (`providers.py` ZAI_DEFAULT_BASE_URL /
 * ZAI_DEFAULT_SMALL_MODEL) — NOT exposed in this UI anymore. The daemon
 * still accepts overriding them via a direct `POST /config/providers` call
 * for an advanced/future use case; this cockpit just never asks for them.
 *
 * This is DAEMON state (one config per machine, NOT per project), so it
 * lives in the General settings drawer, not the per-project Config tab.
 * Contract (py-1.32.1, portal-authed):
 *   GET  /config/providers → { providers[] }  (keyPresent only, never the key)
 *   POST /config/providers → apply partial patch, returns fresh config
 *
 * Security: API keys are WRITE-ONLY here — the daemon stores them chmod-600
 * and never returns their value; we only ever show a `keyPresent` badge.
 * A daemon older than MPV1 404s → the block renders a short "unavailable"
 * note instead of an error.
 */

import { For, Show, createEffect, createSignal } from 'solid-js';
import { daemonStore } from '~/state/daemon';
import { clientsStore } from '~/state/clients';
import type { ProviderConfigInfo, ProvidersConfigPatch, ProvidersConfigResponse } from '~/lib/daemon-client';
import { withAuthRetry } from '~/lib/retry';
import { Block, Toggle } from './atoms';

// Shown (read-only) in the API-key input when a key is already stored —
// the daemon never returns the real value, so this is a generic mask, not
// the actual key's length. Matches the masked-token look already used for
// the Remote-control bearer token. Click "Clear" to make the field
// editable again and paste a new one.
const MASKED_KEY = '••••••••••••••••••••••••';

type CfgState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: ProvidersConfigResponse };

export function ClientsBlock(props: { active?: boolean } = {}) {
  const client = () => daemonStore.state.client;

  // 2026-07-09 fix — this block now mounts at APP BOOT (GeneralConfigDrawer
  // is always in the DOM so its close animation can play), well before the
  // daemon client attaches. A one-shot `createResource(fn)` with no
  // reactive `source` param captured that early `null` client FOREVER and
  // never refetched once the daemon connected — it was permanently stuck
  // showing "upgrade your daemon" even on an already-upgraded, fully
  // connected daemon. Fixed the same way `CredentialsBlock` (V107.16)
  // already solved this: a plain signal refreshed from a `createEffect`
  // keyed on `daemonStore.state.client`, so it reruns the moment the
  // client attaches (or changes on a project switch) — not just once at
  // mount. `loading` is a distinct state (not `unavailable`) so the brief
  // pre-connect window never flashes the "upgrade your daemon" message.
  const [cfg, setCfg] = createSignal<CfgState>({ status: 'loading' });

  const refetch = async (): Promise<void> => {
    const c = client();
    if (!c) {
      setCfg({ status: 'loading' });
      return;
    }
    // `withAuthRetry` absorbs a 401 that happens right after the daemon
    // (re)connects (observed: fails on first load, succeeds a few seconds
    // later on a manual retry) — the operator should never have to click a
    // button for a request that "can't fail"; give it the same few seconds
    // automatically before ever showing an error.
    const r = await withAuthRetry(() => c.providerConfigGet());
    if (r.ok) {
      setCfg({ status: 'ok', data: r.data });
      await clientsStore.hydrate(c);
      return;
    }
    // 404 → daemon too old for this feature; anything else is a real error.
    if (r.status === 404) {
      setCfg({ status: 'unavailable' });
      return;
    }
    setCfg({ status: 'error', message: `HTTP ${r.status}` });
  };

  // DM-CLI-15 — `active` gates on visibility: the General settings drawer
  // is always in the DOM (close animation), so a mount-only fetch goes
  // stale the moment a CLI is installed while the cockpit is up. Passing
  // `active={drawerOpen}` re-probes `GET /config/providers` + `GET
  // /clients` on every open — on demand, no polling, no browser refresh.
  createEffect(() => {
    client(); // establishes the dependency — reruns on attach/project-switch
    if (props.active === false) return; // drawer closed — skip; open reruns us
    void refetch();
  });

  const okData = (): ProvidersConfigResponse | null => {
    const c = cfg();
    return c.status === 'ok' ? c.data : null;
  };
  // DM-CLI-14 — human label for the slot this row shares its credential
  // with (`meta` ↔ `muse`). Falls back to the raw id when the daemon's
  // list doesn't contain it (shouldn't happen, but never render blank).
  const aliasLabel = (id: string | null | undefined): string | null => {
    if (!id) return null;
    return okData()?.providers.find((x) => x.id === id)?.label ?? id;
  };
  // DM-CLI-15 — one vendor, one card. `meta` (API provider) + `muse`
  // (CLI client) share one key + one switch: merge them into a single
  // card, but ONLY when the daemon explicitly links both directions
  // (py-1.35.14+, sharesKeyWith). Older daemons keep the legacy layout
  // (install box + two rows) via the !mergedPair() fallbacks below.
  const metaRow = (): ProviderConfigInfo | null =>
    okData()?.providers.find((x) => x.id === 'meta') ?? null;
  const museRow = (): ProviderConfigInfo | null =>
    okData()?.providers.find((x) => x.id === 'muse') ?? null;
  const mergedPair = (): boolean => {
    const a = metaRow(), b = museRow();
    return !!a && !!b && a.sharesKeyWith === 'muse' && b.sharesKeyWith === 'meta';
  };
  const singleRows = (): ProviderConfigInfo[] =>
    (okData()?.providers ?? []).filter(
      (p) => !mergedPair() || (p.id !== 'meta' && p.id !== 'muse'),
    );
  const museClient = () =>
    clientsStore.state.list.find((c) => c.id === 'muse') ?? null;
  const metaMuseKeyPresent = (): boolean =>
    !!(metaRow()?.keyPresent || museRow()?.keyPresent);
  const metaMuseEnabled = (): boolean =>
    (metaRow()?.enabled ?? true) && (museRow()?.enabled ?? true);
  // The API lane works with a stored key; the CLI lane works with an
  // installed binary that isn't explicitly unauthenticated (null means
  // "can't tell", not "no").
  const metaMuseLaneUp = (): boolean => {
    if (metaMuseKeyPresent()) return true;
    const m = museClient();
    return !!m && m.installed && m.authConfigured !== false;
  };
  const metaMuseAvailable = (): boolean => metaMuseEnabled() && metaMuseLaneUp();
  const cliStatus = (): string => {
    const m = museClient();
    if (!m) return 'CLI status unknown — press Refresh below.';
    if (!m.installed) return 'CLI not installed on daemon host.';
    if (m.authConfigured === true) return 'CLI installed on daemon host, login configured.';
    if (m.authConfigured === false)
      return 'CLI installed on daemon host — login required.';
    return 'CLI installed on daemon host.';
  };
  // One switch drives both slots (the daemon mirrors them too, so either
  // order of daemon works); the key is stored once under `muse`, which
  // the daemon's alias fallback also serves to `meta`.
  const toggleMetaMuse = (enabled: boolean) =>
    void apply(`toggle:meta-muse`, {
      providers: { meta: { enabled }, muse: { enabled } },
    });
  const saveMetaMuse = () => {
    const key = (draft().muse ?? '').trim();
    if (!key) return;
    void apply('save:muse', { providers: { muse: { key } } });
  };
  const clearMetaMuse = () =>
    void apply('clear:muse-meta', {
      providers: { muse: { clear_key: true }, meta: { clear_key: true } },
    });
  const errorMessage = (): string => {
    const c = cfg();
    return c.status === 'error' ? c.message : '';
  };

  const [busy, setBusy] = createSignal<string | null>(null);
  const [err, setErr] = createSignal<string | null>(null);
  // Local edit buffer: just the (write-only) key being typed, keyed by id.
  // 2026-07-09 simplification — operator feedback: base-url/small-model are
  // implementation defaults ("tú ya sabrás cómo conectarte"), not something
  // an operator should ever need to set. The daemon still applies its own
  // sensible defaults (providers.py ZAI_DEFAULT_BASE_URL/SMALL_MODEL); this
  // UI only ever asks for the one thing that's actually the operator's:
  // the API key.
  const [draft, setDraft] = createSignal<Record<string, string>>({});

  const patchKeyDraft = (id: string, key: string) =>
    setDraft((d) => ({ ...d, [id]: key }));

  const apply = async (label: string, body: ProvidersConfigPatch): Promise<void> => {
    const c = client();
    if (!c) return;
    setBusy(label);
    setErr(null);
    const r = await c.providerConfigSet(body);
    setBusy(null);
    if (!r.ok) { setErr(`Save failed (HTTP ${r.status}).`); return; }
    setDraft({}); // clear typed keys — they've been sent
    await refetch();
  };

  const saveProvider = (p: ProviderConfigInfo) => {
    const key = (draft()[p.id] ?? '').trim();
    if (!key) return;
    void apply(`save:${p.id}`, { providers: { [p.id]: { key } } });
  };

  const toggleProvider = (id: string, enabled: boolean) =>
    void apply(`toggle:${id}`, { providers: { [id]: { enabled } } });

  const clearKey = (id: string) =>
    void apply(`clear:${id}`, { providers: { [id]: { clear_key: true } } });

  return (
    <Block
      title="Providers"
      subtitle="Machine-level — one config per Mac, shared by every project. Not per-project."
    >
      <div class="rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-[12px] text-sky-100/90 leading-snug mb-3">
        <span class="font-mono text-[9px] uppercase tracking-wider text-sky-300 bg-sky-500/15 border border-sky-500/30 rounded px-1.5 py-0.5 mr-1.5 align-middle">machine-level</span>
        One key per provider, set ONCE here — shared by every project on this
        Mac, never copied project-to-project. Enable a provider and (if it
        needs one) paste its key — any project's team members can then
        select it. <b>Anthropic</b> needs no key (Claude Code's own Mac login
        handles it); <b>Codex</b>/<b>Gemini</b>/<b>Muse</b> also keep working via their
        own native login even without a key here — the key is just a
        convenience for headless agents.
      </div>

      {/* DM-CLI-15 — legacy install box: only on daemons too old to link
          the two Meta slots (no merged card). New daemons render the
          single "Muse (Meta)" card inside the provider list instead. */}
      <Show when={!mergedPair() && clientsStore.state.list.some((c) => c.id === 'muse')}>
        <div class="rounded-md border border-gray-700/50 p-3 text-[12px] text-gray-300 space-y-2 mb-3">
          <p><b>Muse Code (Meta)</b> — run these commands on the daemon's machine:</p>
          <pre class="overflow-x-auto text-[11px]">curl -fsSL https://dev.meta.ai/install.sh | bash</pre>
          <pre class="overflow-x-auto text-[11px]">muse login</pre>
          <p>Approve the code in your browser with your Meta account. Alternatively, save a Meta API key below. A saved API key takes priority over account login.</p>
          <p>Contributor models may use your content to improve Meta products. The CLI default currently selects Muse Spark 1.3 Contributor; choose a named model in the team editor to override it.</p>
          <p>{(() => {
            const muse = clientsStore.state.list.find((c) => c.id === 'muse');
            return !muse?.installed ? 'Status: not installed on daemon host.'
              : muse.authConfigured ? 'Status: installed and authentication configured.'
                : 'Status: installed; authentication required or Muse disabled below.';
          })()}</p>
          <button type="button" onClick={() => void refetch()} class="text-emerald-300 hover:text-white underline">Refresh installation and login status</button>
          <p><a href="https://dev.meta.ai/docs/muse-code" target="_blank" rel="noopener noreferrer" class="underline">Meta setup documentation</a></p>
        </div>
      </Show>

      <Show when={err()}>
        <div class="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-[12px] text-red-200 mb-3">{err()}</div>
      </Show>

      <Show
        when={okData()}
        fallback={
          <div class="flex items-center justify-between gap-3 text-[12px] text-gray-500 leading-relaxed">
            <p>
              {cfg().status === 'loading'
                ? 'Connecting to the daemon…'
                : cfg().status === 'error'
                  ? errorMessage()
                  : "This daemon doesn't expose provider config yet — upgrade it (py-1.32.1+) to manage providers here."}
            </p>
            <Show when={cfg().status === 'error'}>
              <button
                type="button"
                onClick={() => void refetch()}
                class="flex-shrink-0 text-[11px] font-mono uppercase tracking-wider text-red-200 hover:text-white border border-red-500/40 hover:border-red-400/70 rounded px-2 py-1"
              >Retry</button>
            </Show>
          </div>
        }
      >
        <div class="space-y-4">
          {/* DM-CLI-15 — ONE card per vendor: Meta's API lane + the Muse
              CLI lane share one key + one switch, so they render as one
              "Muse (Meta)" card (install status, key field, docs, refresh)
              instead of two rows + the legacy top box. */}
          <Show when={mergedPair()}>
            <div class="rounded-lg border border-gray-800/60 p-4 space-y-3">
              <div class="flex items-center justify-between gap-2 pb-2.5 border-b border-gray-800/60">
                <div class="flex items-center gap-2">
                  <span class="font-mono text-[13px] text-gray-100">Muse (Meta)</span>
                  <Show when={metaMuseAvailable()}>
                    <span class="font-mono text-[9px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">available</span>
                  </Show>
                  <Show when={!metaMuseAvailable()}>
                    <span class="font-mono text-[9px] uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">unavailable</span>
                  </Show>
                </div>
                <Toggle
                  checked={metaMuseEnabled()}
                  onChange={toggleMetaMuse}
                  disabled={busy() === 'toggle:meta-muse'}
                  label="Enable Muse (Meta)"
                />
              </div>

              <p class="text-[11px] text-gray-500 leading-snug">
                One vendor, two lanes — the local CLI or the API. One key +
                one switch serve both: set the key once below and either
                lane works.
              </p>

              <div class="rounded-md border border-gray-700/50 p-3 text-[12px] text-gray-300 space-y-2">
                <p><b>CLI lane</b> — {cliStatus()}</p>
                <Show when={!museClient()?.installed}>
                  <p>Install the Muse CLI on the daemon host (that machine, not this browser):</p>
                  <pre class="overflow-x-auto text-[11px]">curl -fsSL https://dev.meta.ai/install.sh | bash</pre>
                  <pre class="overflow-x-auto text-[11px]">muse login</pre>
                  <p>Then press Refresh below — no browser reload needed.</p>
                </Show>
                <Show when={museClient()?.installed && museClient()?.authConfigured === false}>
                  <p>Run <span class="font-mono">muse login</span> on the daemon host, then press Refresh below.</p>
                </Show>
                <p>Contributor models may use your content to improve Meta products. The CLI default currently selects Muse Spark 1.3 Contributor; choose a named model in the team editor to override it.</p>
                <p><button type="button" onClick={() => void refetch()} class="text-emerald-300 hover:text-white underline">Refresh installation and login status</button></p>
                <p><a href="https://dev.meta.ai/docs/muse-code" target="_blank" rel="noopener noreferrer" class="underline">Meta setup documentation</a></p>
              </div>

              <div class="space-y-1.5">
                <div class="flex items-center justify-between">
                  <span class="font-mono text-[10px] uppercase tracking-wider text-gray-500">API key <span class="normal-case tracking-normal">· API lane</span></span>
                  <span class="text-[10px]" classList={{ 'text-emerald-400': metaMuseKeyPresent(), 'text-amber-400': !metaMuseKeyPresent() }}>
                    {metaMuseKeyPresent() ? 'key set ✓' : 'no key'}
                  </span>
                </div>
                <div class="flex gap-1.5">
                  <input
                    type="password"
                    value={draft().muse ?? (metaMuseKeyPresent() ? MASKED_KEY : '')}
                    readOnly={metaMuseKeyPresent() && draft().muse === undefined}
                    onInput={(e) => patchKeyDraft('muse', e.currentTarget.value)}
                    placeholder="paste the Meta API key"
                    title={metaMuseKeyPresent() ? 'A key is already set — click Clear to replace it' : undefined}
                    class="flex-1 min-w-0 bg-[#020617] border border-gray-700/40 rounded px-2.5 py-1.5 text-[12px] font-mono text-gray-100 focus:outline-none focus:border-emerald-500/55"
                  />
                  <button
                    type="button"
                    onClick={saveMetaMuse}
                    disabled={busy() !== null || !(draft().muse ?? '').trim()}
                    class="flex-shrink-0 text-[11px] font-mono uppercase tracking-wider text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 rounded px-3 py-1.5 disabled:opacity-50"
                  >{busy() === 'save:muse' ? 'Saving…' : 'Save'}</button>
                  <Show when={metaMuseKeyPresent()}>
                    <button
                      type="button"
                      onClick={clearMetaMuse}
                      disabled={busy() !== null}
                      class="flex-shrink-0 text-[11px] font-mono text-red-300 border border-red-500/30 hover:bg-red-500/10 rounded px-2 py-1.5 disabled:opacity-50"
                      title="Delete the stored key"
                    >Clear</button>
                  </Show>
                </div>
              </div>
            </div>
          </Show>
          <For each={singleRows()}>
            {(p) => (
              <div class="rounded-lg border border-gray-800/60 p-4 space-y-3">
                {/* Title row — clearly delimited from the fields below. */}
                <div class="flex items-center justify-between gap-2 pb-2.5 border-b border-gray-800/60">
                  <div class="flex items-center gap-2">
                    <span class="font-mono text-[13px] text-gray-100">{p.label}</span>
                    <Show when={p.available}>
                      <span class="font-mono text-[9px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5">available</span>
                    </Show>
                    <Show when={!p.available}>
                      <span class="font-mono text-[9px] uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">unavailable</span>
                    </Show>
                    {/* DM-CLI-14 — aliased rows (Meta ↔ Muse) share one key
                        and one switch: say so, so the second row reads as
                        "same credential" instead of a duplicate. */}
                    <Show when={p.sharesKeyWith}>
                      <span class="font-mono text-[9px] normal-case tracking-normal text-gray-500" title="One API key and one on/off switch serve both rows — set it once, either side.">
                        shares key + switch with {aliasLabel(p.sharesKeyWith)}
                      </span>
                    </Show>
                  </div>
                  <Toggle
                    checked={p.enabled}
                    onChange={(v) => toggleProvider(p.id, v)}
                    disabled={busy() === `toggle:${p.id}`}
                    label={`Enable ${p.label}`}
                  />
                </div>

                <Show
                  when={p.requiresKey}
                  fallback={
                    <p class="text-[11px] text-gray-500 leading-snug">
                      Native login/config — no API key needed here.
                    </p>
                  }
                >
                  {/* API key (write-only) — input + Save on the same line. */}
                  <div class="space-y-1.5">
                    <div class="flex items-center justify-between">
                      <span class="font-mono text-[10px] uppercase tracking-wider text-gray-500">API key</span>
                      <span class="text-[10px]" classList={{ 'text-emerald-400': p.keyPresent, 'text-amber-400': !p.keyPresent }}>
                        {p.keyPresent ? 'key set ✓' : 'no key'}
                      </span>
                    </div>
                    <div class="flex gap-1.5">
                      <input
                        type="password"
                        value={draft()[p.id] ?? (p.keyPresent ? MASKED_KEY : '')}
                        readOnly={p.keyPresent && draft()[p.id] === undefined}
                        onInput={(e) => patchKeyDraft(p.id, e.currentTarget.value)}
                        placeholder={`paste the ${p.label} API key`}
                        title={p.keyPresent ? 'A key is already set — click Clear to replace it' : undefined}
                        class="flex-1 min-w-0 bg-[#020617] border border-gray-700/40 rounded px-2.5 py-1.5 text-[12px] font-mono text-gray-100 focus:outline-none focus:border-emerald-500/55"
                      />
                      <button
                        type="button"
                        onClick={() => saveProvider(p)}
                        disabled={busy() !== null || !(draft()[p.id] ?? '').trim()}
                        class="flex-shrink-0 text-[11px] font-mono uppercase tracking-wider text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 rounded px-3 py-1.5 disabled:opacity-50"
                      >{busy() === `save:${p.id}` ? 'Saving…' : 'Save'}</button>
                      <Show when={p.keyPresent}>
                        <button
                          type="button"
                          onClick={() => clearKey(p.id)}
                          disabled={busy() !== null}
                          class="flex-shrink-0 text-[11px] font-mono text-red-300 border border-red-500/30 hover:bg-red-500/10 rounded px-2 py-1.5 disabled:opacity-50"
                          title="Delete the stored key"
                        >Clear</button>
                      </Show>
                    </div>
                  </div>
                </Show>
              </div>
            )}
          </For>
        </div>
      </Show>
    </Block>
  );
}
